import { PropsWithChildren } from "react";
import Page from "~/components/Page";
import ProductAddToCart from "~/components/ProductAddToCart";
import ProductAvailability from "~/components/ProductAvailability";
import ProductBrandLink from "~/components/ProductBrandLink";
import ProductColorSelector from "~/components/ProductColorSelector";
import ProductLinked from "~/components/ProductLinked";
import ProductMedia, {
  mainMediaFrom,
  mediaFrom,
} from "~/components/ProductMedia";
import ProductName from "~/components/ProductName";
import {
  ProductDetail,
  ProductDetailActions,
  ProductDetailBrand,
  ProductDetailMedia,
  ProductDetailSelector,
  ProductDetailTop,
  ProductDetailTopPilot,
} from "~/components/ProductPage";
import ProductPrice from "~/components/ProductPrice";
import { SimpleTrustpilotWidget } from "~/components/TrustpilotWidget";
import { productNameFrom } from "~/entities/product/utils";
import {
  isProductAvailabilityResponseTypesafe,
  isProductTypesafe,
} from "~/entities/product/validators";
import { isUserTypesafe } from "~/entities/user/validators";
import { selectedVariantFrom } from "~/entities/variant/utils";

type Props = {
  product: unknown;
  variantId: string | undefined;
  user: unknown;
  productAvailability: unknown;
};

const ProductError: React.FC<PropsWithChildren<Props>> = ({
  children,
  product: unknownProduct,
  variantId,
  user,
  productAvailability,
}) => {
  const isValidProduct = isProductTypesafe(unknownProduct);
  const isValidProductAvailability =
    isProductAvailabilityResponseTypesafe(productAvailability);
  const isValidUser = isUserTypesafe(user);

  if (!isValidProduct) {
    return <Page className="min-h-[50vh]">{children}</Page>;
  }

  const product = isValidProduct;
  const variant = selectedVariantFrom(
    variantId || product.defaultVariant.id,
    product,
  );
  const initialMainMedia = mainMediaFrom(variant);
  const initialMedia = mediaFrom(variant, product);

  return (
    <Page>
      <ProductDetail>
        <ProductDetailBrand>
          <ProductBrandLink mill={product.mill} />
        </ProductDetailBrand>
        <ProductDetailTop>
          <ProductName name={productNameFrom(product)} />
          <ProductDetailTopPilot isShown={product.reviewable}>
            <SimpleTrustpilotWidget sku={product.styleCode} />
          </ProductDetailTopPilot>
        </ProductDetailTop>
        <ProductDetailMedia>
          <ProductMedia
            initialMedia={initialMedia}
            initialMainMedia={initialMainMedia}
            selectedVariant={variant}
            product={product}
          />
        </ProductDetailMedia>
        <ProductDetailActions>
          <ProductLinked product={product} />
          <ProductPrice product={product} />
          <ProductDetailSelector>
            <ProductColorSelector
              product={product}
              setSelectedVariant={() => null}
              selectedVariant={variant}
            />
          </ProductDetailSelector>
          {isValidProductAvailability ? (
            <ProductAddToCart
              product={product}
              selectedVariant={variant}
              productAvailability={Promise.resolve(isValidProductAvailability)}
              user={isValidUser}
            >
              <ProductAvailability
                product={product}
                isSignedIn={!!user}
                selectedVariant={variant}
              />
            </ProductAddToCart>
          ) : null}
        </ProductDetailActions>
      </ProductDetail>
      {children}
    </Page>
  );
};

export default ProductError;
